import { RouteInfo, getItemId } from '../../classes/routes';
import { configFlagEnabled } from '../../route-builder-v2/utils/feature-flag';

export interface FlattenedNavItem {
	id: string;
	path: string;
	title: string;
	icon: string;
}

export interface FlattenMenuOptions {
	storefrontStandalone?: boolean;
	config?: Record<string, unknown> | null;
}

function showItem(item: { package?: string }, configPackage: string): boolean {
	if (!item.package) return true;
	return item.package === 'all' || item.package === configPackage;
}

export function flattenNavigableItems(
	menuItems: RouteInfo[],
	configPackage: string,
	options?: FlattenMenuOptions
): FlattenedNavItem[] {
	const out: FlattenedNavItem[] = [];
	if (!menuItems) return out;
	const storefrontStandalone = options?.storefrontStandalone === true;
	const runtimeConfig = options?.config ?? null;

	for (const menuItem of menuItems) {
		if (menuItem.storefrontStandaloneOnly && !storefrontStandalone) continue;
		if (!showItem(menuItem, configPackage)) continue;

		if (!menuItem.children || menuItem.children.length === 0) {
			if ((menuItem as RouteInfo).requireConfigTruthy &&
				!configFlagEnabled(runtimeConfig, (menuItem as RouteInfo).requireConfigTruthy)) {
				continue;
			}
			out.push({
				id: getItemId(menuItem.path),
				path: menuItem.path,
				title: menuItem.title,
				icon: menuItem.icon || ''
			});
			continue;
		}

		for (const item of menuItem.children) {
			if ((item as RouteInfo).requireConfigTruthy &&
				!configFlagEnabled(runtimeConfig, (item as RouteInfo).requireConfigTruthy)) {
				continue;
			}
			if ((item as any).hideWhenStorefrontStandalone && storefrontStandalone) continue;
			if (!showItem(item, configPackage)) continue;

			if (!item.children || item.children.length === 0) {
				out.push({
					id: getItemId(item.path),
					path: item.path,
					title: item.title,
					icon: item.icon || ''
				});
			} else {
				for (const ti of item.children) {
					out.push({
						id: getItemId(ti.path),
						path: ti.path,
						title: ti.title || ti.longtitle || '',
						icon: ti.icon || ''
					});
				}
			}
		}

		if (menuItem.reports?.length) {
			for (const r of menuItem.reports) {
				const path = r.path || (r as any).nglink;
				if (path) {
					out.push({
						id: getItemId(path),
						path,
						title: r.title || '',
						icon: r.icon || ''
					});
				}
			}
		}
	}

	return out;
}

const REPORT_TYPE_TO_MENU_ANCHOR: Record<string, string> = {
	customers: 'customers',
	salesmen: 'sales',
	inventory: 'inventory',
	education: 'education',
	generallendger: 'generallendger',
	payables: 'payables',
	warehouse: 'warehouse',
	dispatching: 'dispatching',
	purchases: 'purchases',
	receivables: 'receivables',
	sales: 'sales',
	samples: 'sales-manager-reports',
	system: 'system',
};

export function resolveReportMenuAnchor(report: any, envPackage?: string): string {
	let g = typeof report?.group === 'string' ? report.group.trim() : '';
	if (g === '-' || g === '') {
		const t = report?.type;
		if (typeof t !== 'string' || !t) {
			return '';
		}
		const mapped = REPORT_TYPE_TO_MENU_ANCHOR[t];
		if (!mapped) {
			return '';
		}
		if (t === 'samples' && envPackage !== 'beauty') {
			return 'sales';
		}
		return mapped;
	}
	return g;
}

export function buildAnchorReportsMap(reports: any[], envPackage?: string): Map<string, any[]> {
	const map = new Map<string, any[]>();
	if (!Array.isArray(reports)) {
		return map;
	}
	for (const report of reports) {
		if (!report || typeof report !== 'object' || !('group' in report)) {
			continue;
		}
		const group = resolveReportMenuAnchor(report, envPackage);
		if (!group) {
			continue;
		}
		if (!map.has(group)) {
			map.set(group, []);
		}
		map.get(group)!.push(report);
	}
	return map;
}
